import React, { useContext, useEffect, useState } from 'react'
import { useLoaderData } from 'react-router-dom'
import {
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { isAfter } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { categorizeIssues } from '../../../../components/utils/categorizeIssues'
import useAuth from '../../../../hooks/useAuth'
import InfoBlock from '../../../components/InfoBlock'
import { DelistedHistoryTableRow } from './DelistedHistoryTableRow'
import { IssueHistoryTableRow } from './IssueHistoryTableRow'
import { ListedHistoryTableRow } from './ListedHistoryTableRow'
import { WarrantHistoryTableRow } from './WarrantHistoryTableRow'
import { ConvertibleActionTableRow } from './ConvertibleActionTableRow'
import { findSubscriptionPeriodCloseToDate } from '../../../../utils/warrants'
import { isPreviousWarrant } from '../../../warrants/WarrantsView'
import { GenericHistoryTableRow } from './GenericHistoryTableRow'
import IssueApi from '../../../../api/IssueApi'
import WarrantApi from '../../../../api/WarrantApi'
import { IssueEntity } from '../../../../types/IssueEntity'
import { WarrantEntity } from '../../../../types/WarrantEntity'
import {
    CompanyEntity,
    CompanyShare,
    NameChange,
    NameChangeRowItem,
    ShareBonusIssue,
    ShareSplit,
} from '../../../../types/CompanyEntity'
import { UserMeContext } from '../../../../contexts/UserMeContext'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { ConvertibleAction } from 'types/ConvertibleAction'
import { hasSourceOfType } from '../../../../components/utils/calculateIssueStatus'
import { getIssueDate } from '../../../../utils/issues/getIssueDate'

type ExtendedShareSplit = ShareSplit & { instrumentId: string; symbol: string }
type ExtendedShareBonusIssue = ShareBonusIssue & { instrumentId: string; symbol: string }

type RowItem = (
    | {
          type: 'issue'
          item: IssueEntity
      }
    | {
          type: 'warrant'
          item: WarrantEntity
      }
    | {
          type: 'bonusIssue'
          item: ExtendedShareBonusIssue
      }
    | {
          type: 'split'
          item: ExtendedShareSplit
      }
    | {
          type: 'limitedIpo'
          item: CompanyShare
      }
    | {
          type: 'nameChange'
          item: NameChange
      }
    | {
          type: 'delisted'
          item: CompanyShare
      }
    | {
          type: 'convertibleAction'
          item: ConvertibleAction
      }
) & {
    id: string
    date: string | null
}

function descendingComparator(a: RowItem, b: RowItem) {
    if (b.date === null || a.date === null) return 0

    if (b.date < a.date) {
        return -1
    }
    if (b.date > a.date) {
        return 1
    }
    if (b.date === a.date) {
        if (a.type === 'bonusIssue' && b.type !== 'bonusIssue') {
            return 1
        } else if (b.type === 'bonusIssue' && a.type !== 'bonusIssue') {
            return -1
        }
    }
    return 0
}

function getComparator(order: 'desc' | 'asc') {
    return order === 'desc'
        ? (a: RowItem, b: RowItem) => descendingComparator(a, b)
        : (a: RowItem, b: RowItem) => -descendingComparator(a, b)
}

const getIssueRows = (issues: IssueEntity[]) => {
    const { previousIssues, currentIssues, futureIssues } = categorizeIssues(issues)

    const cancelledIssues = [...currentIssues, ...futureIssues].filter((issue) =>
        hasSourceOfType(issue, 'Cancellation'),
    )

    return [...previousIssues, ...cancelledIssues].map((issue) => {
        return {
            type: 'issue',
            date: getIssueDate(issue),
            id: issue.id,
            item: issue,
        } as RowItem
    })
}

const getWarrantRows = (warrants: WarrantEntity[]) => {
    const today = new Date()

    return warrants
        .filter((warrant) => warrant.cancelled || isPreviousWarrant(today, warrant))
        .map((warrant) => {
            // Find the period with the subEndDate closest to today but still < today
            const closestPeriod = warrant.subscriptionPeriods
                .filter((period) => new Date(period.subEndDate) < today)
                .sort((a, b) => new Date(b.subEndDate) - new Date(a.subEndDate))[0] // Sort by closest to today

            return {
                type: 'warrant',
                date: closestPeriod ? closestPeriod.subEndDate : null,
                id: warrant.id,
                item: warrant,
            } as RowItem
        })
}

const getSplitRows = (shares: CompanyShare[]) => {
    return shares
        .map((share) =>
            (share.splits || []).map(
                (split) =>
                    ({
                        id: share.id + split.changeDate + 'split',
                        item: { ...split, symbol: share.symbol, instrumentId: share.instrumentId },
                        date: split.changeDate,
                        type: 'split',
                    }) as RowItem,
            ),
        )
        .flat()
}

const getConvertibleConversionRows = (convertibles: ConvertibleEntity[] | null) => {
    if (!convertibles) return []

    return convertibles
        .map((convertible) =>
            (convertible.convertibleActions || [])
                .filter((convertibleAction) => convertibleAction.actionType === 'conversion')
                .map(
                    (convertibleAction) =>
                        ({
                            id: convertible.id + convertibleAction.recordDate + 'convertibleAction',
                            item: {
                                ...convertibleAction,
                                symbol: convertible.instrumentSymbol,
                                convertibleName: convertible.convertibleName,
                                instrumentId: convertible.instrumentId,
                            },
                            date: convertibleAction.recordDate,
                            type: 'convertibleAction',
                        }) as RowItem,
                ),
        )
        .flat()
}

const getBonusIssuesRows = (shares: CompanyShare[]) => {
    return shares
        .map((share) =>
            (share.bonusIssues || []).map(
                (bonusIssue) =>
                    ({
                        id: share.id + bonusIssue.changeDate + 'bonus_issue',
                        item: { ...bonusIssue, symbol: share.symbol, instrumentId: share.instrumentId },
                        date: bonusIssue.changeDate,
                        type: 'bonusIssue',
                    }) as RowItem,
            ),
        )
        .flat()
}

const getNameChangeRows = (shares: CompanyShare[]) =>
    shares
        .map((share) =>
            share.nameChanges.map(
                (item) =>
                    ({
                        type: 'nameChange',
                        date: item.date,
                        item,
                    }) as RowItem,
            ),
        )
        .flat()

const getLimitedIpoRows = (shares: CompanyShare[]) => {
    return shares.map(
        (share) =>
            ({
                id: share.id + share.listedDate + 'limitedIpo',
                type: 'limitedIpo',
                date: share.listedDate,
                item: share,
            }) as RowItem,
    )
}
const getDelistedRows = (shares: CompanyShare[]) => {
    return shares
        .filter((share) => share.delistedDate)
        .map(
            (share) =>
                ({
                    id: share.id + share.delistedDate + 'delisted',
                    type: 'delisted',
                    date: share.delistedDate,
                    item: share,
                }) as RowItem,
        )
}

function CompanyHistoryList({ convertibles }: { convertibles: ConvertibleEntity[] | null }) {
    const company = useLoaderData() as CompanyEntity
    const { i18n } = useTranslation()
    const locale = i18n.language
    const { isAuthenticated } = useAuth()

    const [issueRows, setIssueRows] = useState<RowItem[] | null>(null)
    const [warrantRows, setWarrantRows] = useState<RowItem[] | null>(null)
    const [ipoExists, setIpoExists] = useState<boolean | null>(null)
    const [splitAdjust, setSplitAdjust] = useState(false)
    const userMeContext = useContext(UserMeContext)
    const [order, setOrder] = useState<'desc' | 'asc'>(userMeContext.user?.settings.company?.historyOrder || 'asc')

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    const issueApi = new IssueApi()
    const warrantApi = new WarrantApi()

    const splitsRows = getSplitRows(company.shares)
    const bonusIssuesRows = getBonusIssuesRows(company.shares)

    const convertibleConversionsRows = getConvertibleConversionRows(convertibles)

    useEffect(() => {
        if (userMeContext.user?.settings.company?.historyOrder) {
            setOrder(userMeContext.user?.settings.company?.historyOrder)
        }
    }, [userMeContext.user?.settings.company?.historyOrder])

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            try {
                const fetchedIssues = await issueApi.findByCompanyId(company.id)

                const now = new Date().toISOString()
                fetchedIssues.forEach((issue) => {
                    const currentPeriod = findSubscriptionPeriodCloseToDate(issue, now)
                    const issueDate = issue.subStartDate || issue.publicationDate
                    const splitAdjustmentRows = [...splitsRows, ...bonusIssuesRows].filter((split) => {
                        const item = split.item as ExtendedShareSplit | ExtendedShareBonusIssue
                        return (
                            isAfter(new Date(split.date), new Date(issueDate)) &&
                            item.instrumentId === issue.instrumentId
                        )
                    })

                    const splitAdjustment = splitAdjustmentRows.reduce((adjustment, split) => {
                        if (split.type === 'split') {
                            adjustment = (adjustment * split.item.oldShares) / split.item.newShares
                        } else if (split.type === 'bonusIssue') {
                            adjustment =
                                (adjustment * split.item.oldShares) / (split.item.oldShares + split.item.newShares)
                        }

                        return adjustment
                    }, 1)
                    issue.splitAdjustment = splitAdjustment

                    if (currentPeriod) {
                        issue.currentSubscriptionPeriod = currentPeriod
                        issue.subStartDate = currentPeriod.subStartDate
                        issue.subEndDate = currentPeriod.subEndDate
                    }
                })

                setIpoExists(fetchedIssues.some((issue) => issue.type === 'IPO'))

                setIssueRows(getIssueRows(fetchedIssues))
            } catch (e) {
                console.error(e)
            }
        }
        void fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, company])

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            try {
                const fetchedWarrants = await warrantApi.findByCompanyId(company.id)

                const now = new Date().toISOString()
                fetchedWarrants.forEach((warrant) => {
                    const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, now)

                    const warrantDate =
                        warrant.subscriptionPeriods.find((period) => period.subStartDate)?.subStartDate || new Date()

                    const splitAdjustmentRows = [...splitsRows, ...bonusIssuesRows].filter((split) => {
                        const item = split.item as ExtendedShareSplit | ExtendedShareBonusIssue
                        return (
                            isAfter(new Date(split.date), new Date(warrantDate)) &&
                            item.instrumentId === warrant.shareInstrumentId
                        )
                    })

                    const splitAdjustment = splitAdjustmentRows.reduce((adjustment, split) => {
                        if (split.type === 'split') {
                            adjustment = (adjustment * split.item.oldShares) / split.item.newShares
                        } else if (split.type === 'bonusIssue') {
                            adjustment =
                                (adjustment * split.item.oldShares) / (split.item.oldShares + split.item.newShares)
                        }

                        return adjustment
                    }, 1)
                    warrant.splitAdjustment = splitAdjustment

                    if (currentPeriod) {
                        warrant.currentSubscriptionPeriod = currentPeriod
                        warrant.subStartDate = currentPeriod.subStartDate
                        warrant.subEndDate = currentPeriod.subEndDate
                    }
                })

                setWarrantRows(getWarrantRows(fetchedWarrants))
            } catch (e) {
                console.error(e)
            }
        }
        void fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, company])

    useEffect(() => {
        if (!isAuthenticated || !company || !convertibleConversionsRows) return

        convertibleConversionsRows.forEach((convertibleConversion) => {
            const convertibleConversionDate = convertibleConversion.date

            const splitAdjustmentRows = [...splitsRows, ...bonusIssuesRows].filter((split) => {
                const item = split.item as ExtendedShareSplit | ExtendedShareBonusIssue
                return (
                    isAfter(new Date(split.date), new Date(convertibleConversionDate)) &&
                    item.instrumentId === convertibleConversion.instrumentId
                )
            })

            const splitAdjustment = splitAdjustmentRows.reduce((adjustment, split) => {
                if (split.type === 'split') {
                    adjustment = (adjustment * split.item.oldShares) / split.item.newShares
                } else if (split.type === 'bonusIssue') {
                    adjustment = (adjustment * split.item.oldShares) / (split.item.oldShares + split.item.newShares)
                }

                return adjustment
            }, 1)
            convertibleConversion.splitAdjustment = splitAdjustment
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, company, convertibleConversionsRows])

    const sortOrder = () => {
        setOrder((prev) => {
            const newOrder = prev === 'asc' ? 'desc' : 'asc'

            void userMeContext.updateMe({
                settings: {
                    company: {
                        historyOrder: newOrder,
                    },
                },
            })

            return newOrder
        })
    }

    const multipleInstrumentsExists = company?.shares.length > 1
    const limitedIpoRows = getLimitedIpoRows(company.shares)
    const delistedRows = getDelistedRows(company.shares)
    const nameChangeRows = getNameChangeRows(company.shares)

    let content = null
    const rows: RowItem[] = [
        !ipoExists ? limitedIpoRows : [],
        issueRows,
        warrantRows,
        bonusIssuesRows,
        splitsRows,
        nameChangeRows,
        delistedRows,
        convertibleConversionsRows,
    ]
        .filter((arr) => !!arr)
        .flat()

    if (company) {
        content = (
            <Table sx={isSmallScreen ? { marginX: -3 } : null}>
                <TableHead>
                    <TableRow>
                        <TableCell sortDirection={order}>
                            <TableSortLabel active direction={order} onClick={() => sortOrder()}>
                                Datum
                            </TableSortLabel>
                        </TableCell>
                        {multipleInstrumentsExists && !isSmallScreen ? <TableCell>Aktie</TableCell> : null}
                        <TableCell>Händelse</TableCell>
                        {!isSmallScreen ? (
                            <>
                                <TableCell>Teckningskurs</TableCell>
                                <TableCell>Antal tecknade aktier</TableCell>
                            </>
                        ) : null}
                        <TableCell>Belopp i {company.currency}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows?.sort(getComparator(order)).map(({ id, type, item, date }, index) => {
                        if (type === 'limitedIpo') {
                            return (
                                <ListedHistoryTableRow
                                    key={id + index}
                                    date={date}
                                    share={item}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                        if (type === 'issue') {
                            return (
                                <IssueHistoryTableRow
                                    key={id + index}
                                    date={date}
                                    issue={item}
                                    locale={locale}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    splitAdjust={splitAdjust}
                                    isSmallScreen={isSmallScreen}
                                    nameChangeRows={nameChangeRows as NameChangeRowItem[]}
                                />
                            )
                        }
                        if (type === 'warrant') {
                            return (
                                <WarrantHistoryTableRow
                                    key={id + index}
                                    date={date}
                                    warrant={item}
                                    locale={locale}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    splitAdjust={splitAdjust}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                        if (type === 'bonusIssue') {
                            return (
                                <GenericHistoryTableRow
                                    key={id + index}
                                    date={date as string}
                                    content={`Fondemission ${item.newShares}:${item.oldShares}`}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    symbol={item.symbol}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                        if (type === 'split') {
                            return (
                                <GenericHistoryTableRow
                                    key={id + index}
                                    date={date as string}
                                    content={`Aktiesplit ${item.newShares}:${item.oldShares}`}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    symbol={item.symbol}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                        if (type === 'nameChange') {
                            return (
                                <GenericHistoryTableRow
                                    key={`${item.fromName}_${item.toName}_${index}`}
                                    date={date as string}
                                    content={`Namnändring från ${item.fromName} till ${item.toName}`}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                        if (type === 'delisted') {
                            return (
                                <DelistedHistoryTableRow
                                    key={`delisted-${date}-${item.id}`}
                                    date={date}
                                    share={item}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                        if (type === 'convertibleAction') {
                            return (
                                <ConvertibleActionTableRow
                                    key={`convertible-action-${date}-${item.id}`}
                                    locale={locale}
                                    convertibleAction={item}
                                    date={date}
                                    splitAdjust={splitAdjust}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                    isSmallScreen={isSmallScreen}
                                />
                            )
                        }
                    })}
                </TableBody>
            </Table>
        )
    }

    return (
        <InfoBlock
            title={
                <>
                    Historik{' '}
                    {[...splitsRows, ...bonusIssuesRows].length ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={splitAdjust}
                                    onChange={() => setSplitAdjust((prev) => !prev)}
                                    disableRipple
                                />
                            }
                            label='Justera för aktiesplittar'
                            sx={{ pl: 6 }}
                        />
                    ) : null}
                </>
            }
            loadingElement={!company}
        >
            {content}
        </InfoBlock>
    )
}

export default CompanyHistoryList
